import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./AddContractRegister.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDropzone } from "react-dropzone";
import { Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { NumericFormat } from "react-number-format";

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

//formatare numere cu punct cu . pentru mii si , pentru zecimale
const formatNumber = (value) => {
  if (!value && value !== 0) return "";
  const num = typeof value === 'string' ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

//formatare numar pentru exchange rate
const formatExchangeRate = (value) => {
  if (!value && value !== 0) return "";
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString('de-DE', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

const AddContractRegister = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  //state-uri
  const [suppliers, setSuppliers] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [subprojectCodes, setSubprojectCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);
  const [contractTotal, setContractTotal] = useState("");
  const [contractTotalRaw, setContractTotalRaw] = useState(0);
  const [systemTotalPreview, setSystemTotalPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState(null);

  const [formData, setFormData] = useState({
    signin_date: "",
    end_date: "",
    responsible_person: "",
    supplier: "",
    object: "",
    penalties: "",
    payment_terms: "",
    contract_file: null,
    project_code: "",
    subproject_code: "",
    contract_type: "",
  });

  const isoToDate = (iso) => (iso ? parseISO(iso) : null);
  const dateToIso = (date) => (date ? format(date, "yyyy-MM-dd") : "");

  //memo pentru exchange rate
  const exchangeOptionsByCurrencyId = useMemo(() => {
    const map = new Map();
    for (const rate of exchangeRates) {
      const currencyId = rate.currency;
      if (!map.has(currencyId)) {
        map.set(currencyId, []);
      }
      map.get(currencyId).push({
        value: rate.id,
        label: formatExchangeRate(rate.value),
        dateLabel: rate.date || "",
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }
    for (const [, options] of map.entries()) {
      options.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
    }
    return map;
  }, [exchangeRates]);

  const getDefaultExchangeRateOption = useCallback(
    (currencyId) => {
      const options = exchangeOptionsByCurrencyId.get(currencyId) || [];
      return options.length ? options[0] : null;
    },
    [exchangeOptionsByCurrencyId]
  );

  //calcul moneda sistem
  const computeSystemAmount = useCallback(
    (sum, currencyId, exchangeOpt) => {
      if ((!sum && sum !== 0) || !currencyId || !systemCurrencyId) {
        return "";
      }
      const numericSum = Number(sum);
      if (!Number.isFinite(numericSum)) return "";

      if (Number(currencyId) === Number(systemCurrencyId)) {
        return formatNumber(numericSum);
      }

      const rateValue = exchangeOpt?.rateValue;
      if (!rateValue || !Number.isFinite(rateValue)) return "";
      const result = numericSum * rateValue;
      return formatNumber(result);
    },
    [systemCurrencyId]
  );

  const updateSystemPreview = useCallback(
    ({ total, currency, exchangeRate }) => {
      const currencyId = currency?.value ?? null;
      const result = computeSystemAmount(total, currencyId, exchangeRate);
      setSystemTotalPreview(result);
    },
    [computeSystemAmount]
  );

  //fetch subproject codes by project
  const fetchSubprojectCodesByProject = useCallback(async (projectCodeId) => {
    if (!projectCodeId) {
      setSubprojectCodes([]);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/subprojectcodes/by_project/${projectCodeId}/`
      );
      setSubprojectCodes(response.data);
    } catch (error) {
      console.error("Error fetching subproject codes: ", error);
      setSubprojectCodes([]);
    }
  }, []);

  //fetch initial
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [
          suppliersRes,
          projectCodesRes,
          subprojectCodesRes,
          currenciesRes,
          exchangeRatesRes,
          systemCurrencyRes,
        ] = await Promise.all([
          axiosInstance.get("/suppliers/"),
          axiosInstance.get("/code_registries/"),
          axiosInstance.get("/subprojectcodes/"),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
        ]);

        setSuppliers(suppliersRes.data || []);
        setProjectCodes(projectCodesRes.data || []);
        setSubprojectCodes(subprojectCodesRes.data || []);

        const formattedCurrencies = (currenciesRes.data || []).map((currency) => ({
          value: currency.id,
          label: currency.abbreviation,
          abbreviation: currency.abbreviation,
          name: currency.name,
        }));

        setCurrencies(formattedCurrencies);
        setExchangeRates(exchangeRatesRes.data || []);

        if (Array.isArray(systemCurrencyRes.data) && systemCurrencyRes.data.length > 0) {
          const currencyId = systemCurrencyRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId);
          const systemCurrency = formattedCurrencies.find(
            (c) => Number(c.value) === Number(currencyId)
          );
          setSystemCurrencyLabel(systemCurrency?.label || "");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchInitialData();
  }, []);

  //handlere
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);
    let nextExchangeRate = null;    
    if (selectedOption?.value && systemCurrencyId && 
        Number(selectedOption.value) !== Number(systemCurrencyId)) {
      nextExchangeRate = getDefaultExchangeRateOption(selectedOption.value);
    }

    setSelectedExchangeRate(nextExchangeRate);
    updateSystemPreview({
      total: contractTotalRaw,
      currency: selectedOption,
      exchangeRate: nextExchangeRate,
    });
  };

  const handleExchangeRateChange = (selectedOption) => {
    setSelectedExchangeRate(selectedOption);
    updateSystemPreview({
      total: contractTotalRaw,
      currency: selectedCurrency,
      exchangeRate: selectedOption,
    });
  };

  const handleTotalChange = ({ value, floatValue }) => {
    setContractTotal(value);
    setContractTotalRaw(floatValue || 0);
    
    updateSystemPreview({
      total: floatValue || 0,
      currency: selectedCurrency,
      exchangeRate: selectedExchangeRate,
    });
  };

  const handleProjectCodeChange = (selected) => {
    const projectCodeId = selected?.value || "";
    setFormData((prev) => ({
      ...prev,
      project_code: projectCodeId,
      subproject_code: "", //reseteaza cand se schimba project_code
    }));
    fetchSubprojectCodesByProject(projectCodeId);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setUploadedDocument(file);
      setFormData((prev) => ({ ...prev, contract_file: file }));
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setUploadedDocument(file);
      setFileName(file.name);
      setFormData((prev) => ({ ...prev, contract_file: file }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  //validare forma
  const validateForm = () => {
    if (!contractTotalRaw && contractTotalRaw !== 0) {
      alert("Total is required.");
      return false;
    }
    if (!selectedCurrency) {
      alert("Currency is required.");
      return false;
    }
    if (
      selectedCurrency &&
      systemCurrencyId &&
      Number(selectedCurrency.value) !== Number(systemCurrencyId) &&
      !selectedExchangeRate
    ) {
      alert("Exchange Rate is required for non-system currency.");
      return false;
    }
    return true;
  };

  //trimitere date
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submissionData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
        submissionData.append(key, formData[key]);
      }
    });

    const cleanTotal = contractTotalRaw.toFixed(2);
    submissionData.append("total_contract", cleanTotal);
    submissionData.append("currency", selectedCurrency.value);
    if (selectedExchangeRate) {
      submissionData.append("exchange_rate", selectedExchangeRate.value);
    }

    try {
      await axiosInstance.post("/contract-register/", submissionData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate(-1);
    } catch (error) {
      console.error("Error creating contract:", error.response?.data || error.message);
      alert(
        typeof error.response?.data === "object"
          ? JSON.stringify(error.response.data)
          : "Error creating contract."
      );
    }
  };

  const exchangeOptions = selectedCurrency
    ? exchangeOptionsByCurrencyId.get(selectedCurrency.value) || []
    : [];

  const selectedCurrencyIsSystem =
    selectedCurrency &&
    systemCurrencyId &&
    Number(selectedCurrency.value) === Number(systemCurrencyId);

  const projectCodeOptions = projectCodes.map((pc) => ({
    value: pc.id,
    label: pc.code,
  }));

  const subprojectCodeOptions = subprojectCodes.map((sp) => ({
    value: sp.id,
    label: sp.code,
  }));

  const supplierOptions = suppliers.map((sup) => ({
    value: sup.id,
    label: sup.name,
  }));

  return (
    <div className="add-contract-pro">
      {/*container pentru header*/}
      <div className="add-contract-header-wrapper">
        <div className="contract-header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="add-contract-title">Create Procurement Contract</h2>
        </div>
      </div>

      <hr className="title-separator" />

      {/*container pentru formular*/}
      <div className="add-contract-container">
        <form onSubmit={handleSubmit} className="add-contract-form">
          <div className="form-group">
            <label className="label">
              Contract type <span className="required-star">*</span>
            </label>
            <div className="radio-group">
              {["FM", "FE", "FS"].map((type) => (
                <label key={type} className="radio-label">
                  <input
                    type="radio"
                    name="contract_type"
                    value={type}
                    checked={formData.contract_type === type}
                    onChange={handleChange}
                    required
                  />
                  {type === "FM" && "Material Supplier (FM)"}
                  {type === "FE" && "Works Contractor (FE)"}
                  {type === "FS" && "Service Provider (FS)"}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="label">
                Start Contract Date <span className="required-star">*</span>
              </label>
              <div className="datepicker-container">
                <DatePicker
                  selected={isoToDate(formData.signin_date)}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      signin_date: dateToIso(date),
                    }))
                  }
                  dateFormat="dd.MM.yyyy"
                  placeholderText="dd.mm.yyyy"
                  className="input"
                  required
                  autoComplete="off"
                  popperContainer={({ children }) => (
                    <div style={{ position: "fixed", zIndex: 9999 }}>
                      {children}
                    </div>
                  )}
                />
                <span className="calendar-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>

            <div className="form-group">
              <label className="label">Finish Contract Date</label>
              <div className="datepicker-container">
                <DatePicker
                  selected={isoToDate(formData.end_date)}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      end_date: dateToIso(date),
                    }))
                  }
                  dateFormat="dd.MM.yyyy"
                  placeholderText="dd.mm.yyyy"
                  className="input"
                  autoComplete="off"
                  popperContainer={({ children }) => (
                    <div style={{ position: "fixed", zIndex: 9999 }}>
                      {children}
                    </div>
                  )}
                />
                <span className="calendar-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="label">
              Responsible Person <span className="required-star">*</span>
            </label>
            <input
              type="text"
              name="responsible_person"
              value={formData.responsible_person}
              onChange={handleChange}
              className="input"
              required
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="label">Project Code</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Search Project Code..."
                isSearchable
                isClearable
                options={projectCodeOptions}
                value={projectCodeOptions.find(
                  (option) => option.value === formData.project_code
                )}
                onChange={handleProjectCodeChange}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </div>

            <div className="form-group">
              <label className="label">Subproject Code</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select First Project Code"
                isSearchable
                isClearable
                isDisabled={!formData.project_code}
                options={subprojectCodeOptions}
                value={subprojectCodeOptions.find(
                  (option) => option.value === formData.subproject_code
                )}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    subproject_code: selected?.value || "",
                  }))
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="label">
              Supplier <span className="required-star">*</span>
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Search Supplier..."
              isSearchable
              isClearable
              options={supplierOptions}
              value={supplierOptions.find(
                (option) => option.value === formData.supplier
              )}
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  supplier: selected?.value || "",
                }))
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectPortalStyles}
            />
          </div>

          <div className="form-group">
            <label className="label">
              Object <span className="required-star">*</span>
            </label>
            <input
              type="text"
              name="object"
              value={formData.object}
              onChange={handleChange}
              className="input"
              required
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="label">
                Total {selectedCurrency && ` (${selectedCurrency.abbreviation})`}
                 <span className="required-star">*</span>
              </label>
              <NumericFormat
                name="total_contract"
                value={contractTotal}
                onValueChange={handleTotalChange}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">
                Currency <span className="required-star">*</span>
              </label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select currency..."
                isSearchable
                isClearable
                options={currencies}
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="label">Exchange Rate<span className="required-star">*</span></label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder={
                  !selectedCurrency
                    ? "Select currency first"
                    : selectedCurrencyIsSystem
                    ? "Not needed"
                    : exchangeOptions.length
                    ? "Select rate"
                    : "No rates"
                }
                isSearchable
                isClearable
                isDisabled={!selectedCurrency || selectedCurrencyIsSystem}
                options={exchangeOptions}
                value={selectedExchangeRate}
                onChange={handleExchangeRateChange}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                formatOptionLabel={(option, { context }) =>
                  context === "value"
                    ? option.label
                    : `${option.label}${option.dateLabel ? ` (${formatDateLabel(option.dateLabel)})` : ""}`
                }
                styles={selectPortalStyles}
              />
            </div>

            <div className="form-group">
              <label className="label">
                Total System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                <span className="required-star">*</span>
              </label>
              <NumericFormat
                value={systemTotalPreview}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="label">Penalties</label>
              <input
                name="penalties"
                type="text"
                value={formData.penalties}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="form-group">
              <label className="label">
                Payment terms <span className="required-star">*</span>
              </label>
              <input
                name="payment_terms"
                type="text"
                value={formData.payment_terms}
                onChange={handleChange}
                className="input"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="label">
              File Upload <span className="required-star">*</span>
            </label>
            <div className="file-upload-wrapper">
              <input
                type="text"
                value={fileName}
                className="input file-name-input"
                placeholder="Click to select file"
                readOnly
                onClick={() => fileInputRef.current?.click()}
              />
              <button
                type="button"
                className="file-upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachFileIcon />
                <span>Choose File</span>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="file-input-hidden"
              />
            </div>
          </div>

          <div
            {...getRootProps()}
            className={`dropzone-area ${isDragActive ? "dragging" : ""}`}
          >
            <input {...getInputProps()} />
            {uploadedDocument ? (
              <div className="dropzone-content">
                <AttachFileIcon color="primary" />
                <Typography variant="body2">
                  Document "<span className="file-name">{uploadedDocument.name}</span>" attached!
                </Typography>
              </div>
            ) : (
              <Typography variant="body2">
                Drag & drop a file here or click to select
              </Typography>
            )}
          </div>

          <div className="button-group-inline">
            <button type="button" className="button cancel" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className="button save">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContractRegister;